import { queryOptions } from "@tanstack/react-query";

import { apiUrl } from "@workspace/base-api/apiUrl";
import {
	fetchJson,
	type HttpRequestError,
} from "@workspace/base-api/fetchJson";

import type {
	OrderPrintStateResponse,
	PrintJobActionResult,
} from "./print-state-types";

/**
 * HA-575 operator print-job client. The read surface carries the affordance flags, so the UI never
 * decides for itself whether a control should appear — it renders what the backend permits.
 */
export const printJobsApi = {
	async getOrderPrintState(
		tenant: string,
		venueId: string,
		orderId: string,
	): Promise<OrderPrintStateResponse> {
		const url = new URL(
			`/venue-management/${venueId}/epos/orders/${orderId}/print-state`,
			apiUrl,
		);
		return await fetchJson<OrderPrintStateResponse>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	orderPrintStateOptions: (
		tenant: string,
		venueId: string,
		orderId: string,
		enabled = true,
	) =>
		queryOptions({
			queryKey: [tenant, venueId, "epos", "orders", orderId, "print-state"],
			queryFn: () => printJobsApi.getOrderPrintState(tenant, venueId, orderId),
			staleTime: 5_000,
			retry: false,
			enabled,
		}),

	async act(
		tenant: string,
		venueId: string,
		outboundJobId: string,
		action: "cancel" | "retry" | "reprint",
	): Promise<PrintJobActionResult> {
		const url = new URL(
			`/venue-management/${venueId}/epos/print-jobs/${outboundJobId}/${action}`,
			apiUrl,
		);
		try {
			await fetchJson<unknown>(url.toString(), {
				method: "POST",
				headers: { tenant: tenant || "" },
				// A no-body POST still sends `{}` — the endpoints bind an EmptyInput.
				body: JSON.stringify({}),
				timeoutMs: 25_000,
			});
			return { success: true };
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false,
				code: error.problem?.code as string | undefined,
				status: error.problem?.status,
				error: error.message,
			};
		}
	},

	printJobActionOptions: (tenant: string, venueId: string) => ({
		mutationKey: [tenant, venueId, "epos", "print-jobs"],
		mutationFn: (data: {
			outboundJobId: string;
			action: "cancel" | "retry" | "reprint";
		}) => printJobsApi.act(tenant, venueId, data.outboundJobId, data.action),
	}),
};
